import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import { principles, siteStats, voices, contactInfo } from "@/mocks/site";
import { usePageMeta } from "@/hooks/usePageMeta";

const milestones = [
  { year: "2023 秋", title: "从一个表格开始", desc: "最初只是编辑组内部用于核对事件顺序的共享表格，后来发现它对读者同样有用。" },
  { year: "2024 夏", title: "公开第一批时间线", desc: "把整理好的 30 篇事件时间线整理成档案格式对外发布，第一次有了固定读者。" },
  { year: "2025 春", title: "建立存疑标注规则", desc: "明确了证据不足节点的标注方式，不再用模糊表述替代判断。" },
  { year: "2026 至今", title: "归档量突破 200 篇", desc: "形成六个稳定的复盘方向，持续回补早期事件的时间线。" },
];

const ABOUT_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 深夜黑料",
    description: "深夜黑料的品牌定位、内容原则、发展理念与联系方式。",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "深夜黑料 · 事件复盘档案库",
    email: contactInfo.email,
    description: "以时间线为核心的娱乐行业公开事件复盘内容团队。",
  },
];

export default function About() {
  usePageMeta({
    title: "关于我们 | 深夜黑料 事件复盘档案库",
    description:
      "深夜黑料是一个以时间线为核心的娱乐行业公开事件复盘档案库，只整理可查证的公开信息。了解我们的品牌定位、内容原则、发展理念与联系方式。",
    keywords: "深夜黑料,关于深夜黑料,事件复盘原则,内容团队",
    path: "/about",
    jsonLd: ABOUT_JSONLD,
  });

  return (
    <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb items={[{ label: "关于我们" }]} />

      <header className="mt-6 max-w-3xl">
        <p className="flex items-center gap-3 text-xs tracking-[0.32em] text-accent-500">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary-500" />
          ABOUT US
        </p>
        <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          关于深夜黑料
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
          我们做一件很具体的事：把已经发生的公开事件，按时间顺序重新排好。不追热点，不博眼球，只在乎顺序对不对、出处清不清楚。
        </p>
      </header>

      <section className="mt-12 rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              网站定位
            </h2>
            <div className="mt-5 space-y-4 text-sm leading-[1.9] text-foreground-700">
              <p>
                深夜黑料是一个内容型档案站，面向想一次性看清事件全貌的读者。我们不做即时资讯，也不生产立场化评论，只把零散的公开信息按时间归位，让事件的起因、转折和结果重新连成一条线。
              </p>
              <p>
                之所以采用「档案」这个形式，是因为大多数争议并不复杂，乱的是信息出现的先后被搅在一起。当顺序被摆正，很多原本看起来矛盾的说法，其实都能各自找到位置。
              </p>
              <p>
                我们不披露任何未公开的私人信息，不对无法核实的内容做推测，也不为流量制造结论。这是这个站点存在的全部理由。
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              {siteStats.map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200/70 bg-background-50 p-4">
                  <p className="font-heading text-2xl font-bold text-accent-400">{stat.value}</p>
                  <p className="mt-2 text-xs text-foreground-700">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mt-16">
        <SectionHeading
          eyebrow="PRINCIPLES"
          title="四条内容原则"
          description="这些规则决定了什么能进档案、什么必须标注存疑，也决定了我们不做哪些事。"
        />
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {principles.map((item) => (
            <div
              key={item.title}
              className="flex h-full flex-col rounded-lg border border-background-200/70 bg-background-100 p-5"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-950 text-accent-400">
                <i className={`${item.icon} text-xl`} />
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-4">
          <SectionHeading eyebrow="MILESTONES" title="发展理念" description="不急于扩张，先把方法做扎实。" />
        </div>
        <div className="lg:col-span-8">
          <ol className="relative space-y-6 border-l border-background-300/60 pl-7">
            {milestones.map((item) => (
              <li key={item.year} className="relative">
                <span className="absolute -left-[35px] top-1.5 h-3.5 w-3.5 rounded-full bg-accent-500 ring-4 ring-background-50" />
                <p className="font-heading text-xs tracking-[0.2em] text-accent-400">{item.year}</p>
                <h3 className="mt-1.5 font-heading text-base font-semibold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="mt-16">
        <SectionHeading
          eyebrow="VOICES"
          title="读者口碑"
          description="长期读者与业界朋友对这套写法的反馈。"
        />
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {voices.map((item) => (
            <figure key={item.name} className="flex h-full flex-col rounded-lg border border-background-200/70 bg-background-100 p-6">
              <i className="ri-double-quotes-l mb-4 text-3xl text-accent-500" />
              <blockquote className="flex-1 text-sm leading-relaxed text-foreground-700">
                {item.text}
              </blockquote>
              <figcaption className="mt-5 border-t border-background-200/70 pt-4 text-xs text-foreground-500">
                <span className="text-foreground-900">{item.name}</span> · {item.role}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="mt-16 rounded-lg border border-accent-900/50 bg-gradient-to-br from-primary-950 to-background-100 p-6 md:p-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              有线索想补充，或想谈谈合作？
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
              无论是纠错、投稿还是内容合作，都可以直接联系我们。
              <span className="ml-1 text-foreground-500">{contactInfo.responseTime}</span>
            </p>
          </div>
          <Link
            to="/contact"
            className="shrink-0 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-background-950 transition-colors duration-200 hover:bg-accent-400 cursor-pointer"
          >
            前往联系页面
          </Link>
        </div>
      </section>
    </div>
  );
}